import { useState, useEffect } from "react";
import {
  ChevronLeft,
  Minus,
  Plus,
  ShoppingCart,
  Trash2,
  Check,
  CheckCircle,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { AuthService } from "../../services/authService";
import { productsServices } from "../../services/productsServices";
import { useOrderWhatsApp } from "../../hooks/useOrderWhatsApp";
import ShippingCalculator from "../../components/ShippingCalculator/ShippingCalculator";
import CartBlocker from "../../components/cart-blocker/cart-blocker";

export function Cart() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [deliveryOption, setDeliveryOption] = useState("retirada");
  const [addressData, setAddressData] = useState({
    id: "",
    street: "",
    number: "",
    complement: "",
    neighborhood: "",
    city: "",
    state: "",
    zipCode: "",
  });
  const [userInfo, setUserInfo] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [produtos, setProdutos] = useState([]);

  const [showConfirmation, setShowConfirmation] = useState(false);
  const { createOrder, loading, error, orderData } = useOrderWhatsApp();
  const [valorFrete, setValorFrete] = useState(0);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Função para selecionar/deselecionar todos os itens
  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedItems([]);
    } else {
      setSelectedItems(produtos.map((item) => item.id));
    }
    setSelectAll(!selectAll);
  };

  // Função para selecionar/deselecionar um item específico
  const toggleSelectItem = (itemId) => {
    if (selectedItems.includes(itemId)) {
      setSelectedItems(selectedItems.filter((id) => id !== itemId));
      setSelectAll(false);
    } else {
      setSelectedItems([...selectedItems, itemId]);
    }
  };

  // Função para remover itens selecionados
  const removeSelectedItems = () => {
    const updatedProdutos = produtos.filter(
      (produto) => !selectedItems.includes(produto.id)
    );

    setProdutos(updatedProdutos);
    setSelectedItems([]);
    setSelectAll(false);

    // Remove do localStorage também:
    removeFromLocalStorage(selectedItems);
  };

  // Função para aumentar quantidade dos itens selecionados
  const increaseQuantitySelected = () => {
    setProdutos(
      produtos.map((produto) =>
        selectedItems.includes(produto.id)
          ? { ...produto, quantidade: produto.quantidade + 1 }
          : produto
      )
    );
  };

  // Função para diminuir quantidade dos itens selecionados
  const decreaseQuantitySelected = () => {
    setProdutos(
      produtos.map((produto) =>
        selectedItems.includes(produto.id) && produto.quantidade > 1
          ? { ...produto, quantidade: produto.quantidade - 1 }
          : produto
      )
    );
  };
  const handleCreateOrder = async () => {
    try {
      const itemsToSend =
        selectedItems.length > 0
          ? produtos.filter((p) => selectedItems.includes(p.id))
          : produtos;

      const order = await createOrder({
        items: itemsToSend.map((item) => ({
          product_id: item.id,
          quantity: item.quantidade,
        })),
        shipping_address: addressData.id,
        customer_phone: userInfo.phone,
      });

      console.log("Pedido criado:", order);
    } catch (error) {
      console.error("Erro:", error);
    }
  };
  // Load authentication and user info
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const authStatus = await AuthService.isAuthenticated();
        setIsAuthenticated(authStatus);
        if (authStatus) {
          const user = await AuthService.getUser();
          setUserInfo({
            name: user.name || "",
            email: user.email || "",
          });
        }
      } catch (error) {
        console.error("Authentication error:", error);
      }
    };
    checkAuth();
  }, []);

  // Load cart from localStorage
  useEffect(() => {
    const loadCart = async () => {
      try {
        const cartData = localStorage.getItem("cartCache");
        if (cartData) {
          const parsedCart = JSON.parse(cartData);

          // Monta os produtos e busca imagens faltantes
          const produtosComImagens = await Promise.all(
            parsedCart.map(async (item) => {
              let imagem = item.image;

              const product = await productsServices.getProductById(item.id);
              console.log(product);
              return {
                id: item.id || product.id,
                nome: item.name || product.name,
                preco: parseFloat(item.price) || parseFloat(product.price) || 0,
                discount: product.discount_percentage || 0,
                quantidade: product.quantity || product.quantidade || 1,
                imagem,
              };
            })
          );

          setProdutos(produtosComImagens);
        }
      } catch (error) {
        console.error("Error loading cart:", error);
      }
    };

    loadCart();
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    const saveCart = () => {
      try {
        const cartToSave = produtos.map((item) => ({
          id: item.id,
          name: item.nome,
          price: item.preco.toString(),
          discount: item.discount || 0,
          quantity: item.quantidade,
          image: item.imagem,
        }));
        localStorage.setItem("cartCache", JSON.stringify(cartToSave));
      } catch (error) {
        console.error("Error saving cart:", error);
      }
    };

    if (produtos.length > 0) {
      saveCart();
    }
  }, [produtos]);

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setAddressData((prev) => ({ ...prev, [name]: value }));
  };

  const aumentarQuantidade = (id) => {
    setProdutos(
      produtos.map((produto) =>
        produto.id === id
          ? { ...produto, quantidade: produto.quantidade + 1 }
          : produto
      )
    );
  };

  const diminuirQuantidade = (id) => {
    setProdutos(
      produtos.map((produto) =>
        produto.id === id && produto.quantidade > 1
          ? { ...produto, quantidade: produto.quantidade - 1 }
          : produto
      )
    );
  };
  const removeFromLocalStorage = (idsToRemove) => {
    try {
      const currentCart = JSON.parse(localStorage.getItem("cartCache")) || [];

      const updatedCart = currentCart.filter(
        (item) => !idsToRemove.includes(item.id)
      );

      localStorage.setItem("cartCache", JSON.stringify(updatedCart));
    } catch (error) {
      console.error("Erro ao remover produtos do localStorage:", error);
    }
  };

  useEffect(() => {
    if (selectedItems.length === produtos.length && produtos.length > 0) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  }, [selectedItems, produtos]);

  const calcularSubtotal = () => {
    return produtos.reduce(
      (total, produto) =>
        selectedItems.includes(produto.id)
          ? total +
            Number(produto.preco - produto.preco * (produto.discount / 100)) *
              produto.quantidade
          : total,
      0
    );
  };

  const subtotal = calcularSubtotal();
  const total = subtotal;

  const enviarParaWhatsApp = () => {
    if (produtos.length === 0) return;

    const itemsToSend =
      selectedItems.length > 0
        ? produtos.filter((p) => selectedItems.includes(p.id))
        : produtos;

    const numeroWhatsApp = "5561984497981";
    let mensagem = `Olá, gostaria de finalizar minha compra:\n\n`;

    mensagem += `*Cliente:* ${userInfo.name}\n`;
    mensagem += `*Email:* ${userInfo.email}\n\n`;

    mensagem += `*Modo de Entrega:* ${
      deliveryOption === "retirada" ? "Retirada na Loja" : "Entrega"
    }\n`;

    if (deliveryOption === "frete") {
      mensagem += `*Endereço de Entrega:*\n`;
      mensagem += `Rua: ${addressData.street}, ${addressData.number}\n`;
      if (addressData.complement)
        mensagem += `Complemento: ${addressData.complement}\n`;
      mensagem += `Bairro: ${addressData.neighborhood}\n`;
      mensagem += `Cidade: ${addressData.city}, ${addressData.state}\n`;
      mensagem += `CEP: ${addressData.zipCode}\n\n`;
    }

    mensagem += `*Itens do Pedido:*\n`;
    itemsToSend.forEach((produto) => {
      const precoComDesconto = Number(
        produto.preco - produto.preco * (produto.discount / 100)
      );
      mensagem += `- ${produto.nome}\n`;
      mensagem += `  Quantidade: ${produto.quantidade}\n`;
      mensagem += `  Preço original: R$ ${produto.preco.toFixed(2)}\n`;
      if (produto.discount > 0) {
        mensagem += `  Desconto: ${produto.discount}%\n`;
        mensagem += `  Preço com desconto: R$ ${precoComDesconto.toFixed(2)}\n`;
      }
      mensagem += `  Subtotal: R$ ${(
        precoComDesconto * produto.quantidade
      ).toFixed(2)}\n\n`;
    });

    const totalPedido = itemsToSend.reduce(
      (total, produto) =>
        total +
        Number(produto.preco - produto.preco * (produto.discount / 100)) *
          produto.quantidade,
      0
    );

    mensagem += `*Total da compra: R$ ${totalPedido.toFixed(2)}*\n\n`;
    mensagem += `Por favor, confirme os itens e me informe as formas de pagamento disponíveis.`;

    const mensagemCodificada = encodeURIComponent(mensagem);
    window.open(
      `https://wa.me/${numeroWhatsApp}?text=${mensagemCodificada}`,
      "_blank"
    );
    setShowConfirmation(false);
  };

  const handleShippingCalculated = (frete) => {
    setValorFrete(frete);
  };

  const renderConfirmationModal = () => (
    <AnimatePresence>
      {showConfirmation && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowConfirmation(false)}
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold mb-4 text-green-600">
              Confirmação de Compra
            </h3>

            <div className="mb-4">
              <label className="block font-medium mb-2">Modo de Entrega:</label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    checked={deliveryOption === "retirada"}
                    onChange={() => setDeliveryOption("retirada")}
                    className="mr-2"
                  />
                  Retirada na Loja
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    checked={deliveryOption === "frete"}
                    onChange={() => setDeliveryOption("frete")}
                    className="mr-2"
                  />
                  Entrega
                </label>
              </div>
            </div>

            {deliveryOption === "frete" && (
              <div className="mb-4 space-y-3">
                <h4 className="font-medium">Endereço de Entrega:</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <input
                      type="text"
                      name="street"
                      placeholder="Rua"
                      value={addressData.street}
                      onChange={handleAddressChange}
                      className="w-full p-2 border rounded"
                      required
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      name="number"
                      placeholder="Número"
                      value={addressData.number}
                      onChange={handleAddressChange}
                      className="w-full p-2 border rounded"
                      required
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      name="complement"
                      placeholder="Complemento"
                      value={addressData.complement}
                      onChange={handleAddressChange}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      name="neighborhood"
                      placeholder="Bairro"
                      value={addressData.neighborhood}
                      onChange={handleAddressChange}
                      className="w-full p-2 border rounded"
                      required
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      name="city"
                      placeholder="Cidade"
                      value={addressData.city}
                      onChange={handleAddressChange}
                      className="w-full p-2 border rounded"
                      required
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      name="state"
                      placeholder="Estado"
                      value={addressData.state}
                      onChange={handleAddressChange}
                      className="w-full p-2 border rounded"
                      required
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      name="zipCode"
                      placeholder="CEP"
                      value={addressData.zipCode}
                      onChange={handleAddressChange}
                      className="w-full p-2 border rounded"
                      required
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold mb-2">Resumo do Pedido:</h4>
              <ul className="space-y-2">
                {(selectedItems.length > 0
                  ? produtos.filter((p) => selectedItems.includes(p.id))
                  : produtos
                ).map((produto) => {
                  const precoComDesconto = Number(
                    produto.preco - produto.preco * (produto.discount / 100)
                  );
                  return (
                    <li key={produto.id} className="flex justify-between">
                      <span>
                        {produto.quantidade}x {produto.nome}
                      </span>
                      <div className="flex flex-col items-end">
                        {produto.discount > 0 && (
                          <span className="text-sm text-gray-500 line-through">
                            R$ {(produto.preco * produto.quantidade).toFixed(2)}
                          </span>
                        )}
                        <span>
                          R${" "}
                          {(precoComDesconto * produto.quantidade).toFixed(2)}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
              <div className="mt-3 pt-2 border-t border-gray-200 font-bold flex justify-between">
                <span>Total:</span>
                <span className="text-green-600">
                  R${" "}
                  {deliveryOption === "frete"
                    ? (total + valorFrete).toFixed(2)
                    : total.toFixed(2)}
                </span>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowConfirmation(false)}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  if (deliveryOption === "frete" && !addressData.street) {
                    alert("Por favor, preencha o endereço de entrega.");
                    return;
                  }
                  useOrderWhatsApp.createOrder();
                  enviarParaWhatsApp();
                }}
                disabled={deliveryOption === "frete" && !addressData.street}
                className={`px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 ${
                  deliveryOption === "frete" && !addressData.street
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                Confirmar e Ir para WhatsApp
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <div className="container mx-auto px-4 py-8 mt-0 md:mt-14 ">
      {renderConfirmationModal()}

      {!isAuthenticated ? (
        <CartBlocker />
      ) : (
        <>
          <header className="mb-8">
            <h1 className="text-3xl font-bold text-green-600 flex items-center gap-2">
              <ShoppingCart className="h-8 w-8" />
              Meu Carrinho
            </h1>
            <p className="text-gray-600 mt-2">Confira e finalize sua compra</p>
          </header>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              {produtos.length > 0 ? (
                <div className="space-y-4">
                  {/* Controles de seleção em massa */}
                  <div className="flex items-center justify-between bg-white p-3 rounded-lg shadow">
                    <div className="flex items-center">
                      <button
                        onClick={toggleSelectAll}
                        className={`p-2 rounded-full mr-2 ${
                          selectAll
                            ? "bg-green-100 text-green-600"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {selectAll ? (
                          <CheckCircle className="h-5 w-5" />
                        ) : (
                          <Check className="h-5 w-5" />
                        )}
                      </button>
                      <span className="font-medium">
                        {selectAll ? "Deselecionar todos" : "Selecionar todos"}
                      </span>
                    </div>

                    {selectedItems.length > 0 && (
                      <div className="flex gap-2">
                        <button
                          onClick={increaseQuantitySelected}
                          className="p-2 bg-green-100 text-green-600 rounded-full hover:bg-green-200"
                          title="Aumentar quantidade"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                        <button
                          onClick={decreaseQuantitySelected}
                          className="p-2 bg-green-100 text-green-600 rounded-full hover:bg-green-200"
                          title="Diminuir quantidade"
                          disabled={selectedItems.some((id) => {
                            const item = produtos.find((p) => p.id === id);
                            return item.quantidade <= 1;
                          })}
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                        <button
                          onClick={removeSelectedItems}
                          className="p-2 bg-red-100 text-red-600 rounded-full hover:bg-red-200"
                          title="Remover selecionados"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Lista de produtos */}
                  {produtos.map((produto) => {
                    const precoComDesconto = Number(
                      produto.preco - produto.preco * (produto.discount / 100)
                    );
                    const isSelected = selectedItems.includes(produto.id);

                    return (
                      <motion.div
                        key={produto.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: -100 }}
                        transition={{ duration: 0.3 }}
                        className={`overflow-hidden border-l-4 ${
                          isSelected
                            ? "border-l-green-600 bg-green-50"
                            : "border-l-green-500 bg-white"
                        } rounded-lg shadow`}
                      >
                        <div className="p-4">
                          <div className="flex flex-col sm:flex-row gap-4">
                            {/* Checkbox de seleção */}
                            <div className="flex items-start">
                              <button
                                onClick={() => toggleSelectItem(produto.id)}
                                className={`p-2 rounded-full mt-1 ${
                                  isSelected
                                    ? "bg-green-100 text-green-600"
                                    : "bg-gray-100 text-gray-600"
                                }`}
                              >
                                {isSelected ? (
                                  <CheckCircle className="h-5 w-5" />
                                ) : (
                                  <Check className="h-5 w-5" />
                                )}
                              </button>
                            </div>

                            {/* Imagem do produto */}
                            <div className="flex-shrink-0 bg-gray-100 rounded-md p-2 w-[100px] h-[100px] flex items-center justify-center">
                              <img
                                src={produto.imagem}
                                alt={produto.nome}
                                width={80}
                                height={80}
                                className="object-contain"
                              />
                            </div>

                            {/* Detalhes do produto */}
                            <div className="flex-grow">
                              <h3 className="font-semibold text-lg">
                                {produto.nome}
                              </h3>
                              <p className="text-gray-600 text-sm">
                                {produto.descricao}
                              </p>
                              <div className="mt-2">
                                {produto.discount > 0 && (
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-red-600 line-through">
                                      R$ {produto.preco.toFixed(2)}
                                    </span>
                                    {/* <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                  {produto.discount}% OFF
                                </span> */}
                                  </div>
                                )}
                                <div className="text-green-600 font-bold">
                                  R$ {precoComDesconto.toFixed(2)}
                                </div>
                              </div>
                            </div>

                            {/* Controles individuais */}
                            <div className="flex flex-col justify-between items-end">
                              <div className="flex items-center border rounded-md">
                                <motion.button
                                  whileTap={{ scale: 0.9 }}
                                  onClick={() => diminuirQuantidade(produto.id)}
                                  disabled={produto.quantidade <= 1}
                                  className="h-8 w-8 text-green-600 p-1"
                                >
                                  <Minus className="h-4 w-4" />
                                  <span className="sr-only">
                                    Diminuir quantidade
                                  </span>
                                </motion.button>
                                <motion.span
                                  key={`qtd-${produto.id}`}
                                  initial={{ scale: 1.2 }}
                                  animate={{ scale: 1 }}
                                  className="w-8 text-center"
                                >
                                  {produto.quantidade}
                                </motion.span>
                                <motion.button
                                  whileTap={{ scale: 0.9 }}
                                  onClick={() => aumentarQuantidade(produto.id)}
                                  className="h-8 w-8 text-green-600 p-1"
                                >
                                  <Plus className="h-4 w-4" />
                                  <span className="sr-only">
                                    Aumentar quantidade
                                  </span>
                                </motion.button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-12 bg-gray-50 rounded-lg"
                >
                  <ShoppingCart className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <h2 className="text-xl font-semibold text-gray-600 mb-2">
                    Seu carrinho está vazio
                  </h2>
                  <p className="text-gray-500 mb-6">
                    Adicione produtos para continuar suas compras
                  </p>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => navigate("/")}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md"
                  >
                    Explorar produtos
                  </motion.button>
                </motion.div>
              )}

              <div className="mt-6">
                <button
                  onClick={() => navigate("/")}
                  className="inline-flex items-center text-green-600 hover:text-green-800 font-medium"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Continuar comprando
                </button>
              </div>
            </div>

            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white shadow-md rounded-lg"
              >
                <div className="p-6">
                  <h2 className="text-xl font-bold mb-4 text-green-600">
                    Resumo do Pedido
                  </h2>

                  {selectedItems.length > 0 && (
                    <div className="mb-2 text-sm text-green-600">
                      Mostrando apenas itens selecionados (
                      {selectedItems.length} de {produtos.length})
                    </div>
                  )}

                  <div className="space-y-3 mb-4">
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        Subtotal (
                        {selectedItems.length > 0
                          ? selectedItems.length
                          : produtos.length}{" "}
                        itens)
                      </span>
                      <span>R$ {calcularSubtotal().toFixed(2)}</span>
                    </div>

                    {produtos.some(
                      (p) =>
                        p.discount > 0 &&
                        (selectedItems.length === 0 ||
                          selectedItems.includes(p.id))
                    ) && (
                      <div className="flex justify-between text-green-600">
                        <span>Descontos aplicados</span>
                        <span>
                          - R${" "}
                          {produtos
                            .reduce(
                              (total, produto) =>
                                selectedItems.length === 0 ||
                                selectedItems.includes(produto.id)
                                  ? total +
                                    produto.preco *
                                      (produto.discount / 100) *
                                      produto.quantidade
                                  : total,
                              0
                            )
                            .toFixed(2)}
                        </span>
                      </div>
                    )}

                    <div className="flex justify-between">
                      <span className="text-gray-600">Frete</span>
                      <span>R$ {valorFrete.toFixed(2)}</span>
                    </div>
                  </div>

                  <hr className="my-4" />

                  <div className="flex justify-between font-bold text-lg mb-6">
                    <span>Total</span>
                    <AnimatePresence mode="wait">
                      <motion.span
                        key={`total-${selectedItems.length}`}
                        initial={{ scale: 1.2, color: "#16a34a" }}
                        animate={{ scale: 1, color: "#16a34a" }}
                        exit={{ scale: 0.8, opacity: 0 }}
                        transition={{ type: "spring", stiffness: 300 }}
                        className="text-green-600"
                      >
                        R$ {(calcularSubtotal() + valorFrete).toFixed(2)}
                      </motion.span>
                    </AnimatePresence>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      if (produtos.length > 0) {
                        if (isAuthenticated) {
                          setShowConfirmation(true);
                        } else {
                          navigate("/login");
                        }
                      }
                    }}
                    disabled={produtos.length === 0}
                    className={`w-full py-3 rounded-md font-medium ${
                      produtos.length > 0
                        ? "bg-green-600 hover:bg-green-700 text-white"
                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                    }`}
                  >
                    Finalizar Compra
                  </motion.button>
                  <div className="mt-4 text-xs text-gray-500 text-center">
                    <p>Pagamento 100% seguro</p>
                    <p>Aceitamos todos os cartões de crédito e PIX</p>
                  </div>
                  <ShippingCalculator
                    onShippingCalculated={handleShippingCalculated}
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
